import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { HostAppShell } from '../components/HostAppShell';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  HeadphonesIcon, Send, Clock, CheckCircle, Loader2,
  AlertTriangle, Zap, MessageSquare, Plus, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_STYLE = {
  open: { color: 'bg-blue-100 text-blue-700 border-blue-200', label: 'Open' },
  in_progress: { color: 'bg-amber-100 text-amber-700 border-amber-200', label: 'In Progress' },
  resolved: { color: 'bg-green-100 text-green-700 border-green-200', label: 'Resolved' },
};

const PRIORITY_STYLE = {
  normal: { color: 'bg-slate-100 text-slate-600 border-slate-200', label: 'Standard' },
  high: { color: 'bg-violet-100 text-violet-700 border-violet-200', label: 'Priority' },
  urgent: { color: 'bg-red-100 text-red-700 border-red-200', label: 'Urgent' },
};

const TIER_CONFIG = {
  free: {
    badge: null,
    message: 'Standard support — responses within 48 hours.',
    responseTime: '~48h',
    canSetUrgent: false,
  },
  basic: {
    badge: null,
    message: 'Standard support — responses within 48 hours.',
    responseTime: '~48h',
    canSetUrgent: false,
  },
  pro: {
    badge: { label: 'Priority Support', color: 'bg-violet-100 text-violet-700 border-violet-200' },
    message: 'You are in the priority queue. Faster response times apply.',
    responseTime: '<24h',
    canSetUrgent: false,
  },
  premium: {
    badge: { label: 'Dedicated Priority', color: 'bg-amber-100 text-amber-700 border-amber-200' },
    message: 'Your requests are handled with highest priority.',
    responseTime: '<4h',
    canSetUrgent: true,
  },
};

const HostSupportCenterPage = () => {
  const { api, getSubscriptionTier, hasFeature } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState('all');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState('');

  const tier = getSubscriptionTier();
  const tierConfig = TIER_CONFIG[tier] || TIER_CONFIG.free;

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const url = filter !== 'all' ? `/host/support/tickets?status=${filter}` : '/host/support/tickets';
      const res = await api.get(url);
      setTickets(res.data.tickets || []);
      setCounts(res.data.counts || {});
    } catch {
      toast.error('Could not load tickets');
    }
    setLoading(false);
  };

  useEffect(() => { fetchTickets(); }, [filter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { subject, message };
      if (tierConfig.canSetUrgent && priority === 'urgent') {
        payload.priority = 'urgent';
      }
      await api.post('/host/support/ticket', payload);
      toast.success('Support ticket submitted');
      setSubject('');
      setMessage('');
      setPriority('');
      setShowForm(false);
      fetchTickets();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit ticket');
    }
    setSubmitting(false);
  };

  return (
    <HostAppShell title="Support Center" activeId="priority_support">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="support-header">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Support Center</h1>
            <p className="text-sm text-slate-500 mt-1">{tierConfig.message}</p>
          </div>
          <div className="flex items-center gap-2">
            {tierConfig.badge && (
              <Badge className={`text-xs border ${tierConfig.badge.color}`} data-testid="support-tier-badge">
                <Zap className="w-3 h-3 mr-1" />
                {tierConfig.badge.label}
              </Badge>
            )}
            <Badge variant="outline" className="text-xs text-slate-500" data-testid="response-time-badge">
              <Clock className="w-3 h-3 mr-1" />
              Est. {tierConfig.responseTime}
            </Badge>
          </div>
        </div>

        {/* Upgrade teaser for free/basic */}
        {(tier === 'free' || tier === 'basic') && (
          <Card className="border-dashed border-violet-200 bg-violet-50/30" data-testid="support-upgrade-teaser">
            <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <HeadphonesIcon className="w-5 h-5 text-violet-500 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-700">Get faster support with a Pro plan</p>
                <p className="text-xs text-slate-500">Priority queue, faster responses, and dedicated support on Premium.</p>
              </div>
              <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-xs h-8" onClick={() => navigate('/host/plan')} data-testid="support-upgrade-btn">
                View Plans
              </Button>
            </CardContent>
          </Card>
        )}

        {/* New Ticket Button / Form */}
        {!showForm ? (
          <Button onClick={() => setShowForm(true)} className="bg-[#004B8D] hover:bg-[#003a6d] h-10 text-sm" data-testid="new-ticket-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Support Ticket
          </Button>
        ) : (
          <Card className="border-slate-200" data-testid="ticket-form">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Send className="w-4 h-4 text-[#004B8D]" />
                New Ticket
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Subject</label>
                  <input
                    type="text"
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    placeholder="Brief summary of your issue"
                    className="w-full h-10 px-3 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#004B8D] focus:border-transparent"
                    required
                    minLength={3}
                    data-testid="ticket-subject-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Message</label>
                  <textarea
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    placeholder="Describe your issue in detail..."
                    className="w-full h-28 px-3 py-2 text-sm border border-slate-200 rounded-lg resize-none focus:ring-2 focus:ring-[#004B8D] focus:border-transparent"
                    required
                    minLength={10}
                    data-testid="ticket-message-input"
                  />
                </div>
                {tierConfig.canSetUrgent && (
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-1">Priority</label>
                    <select
                      value={priority}
                      onChange={e => setPriority(e.target.value)}
                      className="w-full h-10 px-3 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#004B8D] focus:border-transparent"
                      data-testid="ticket-priority-select"
                    >
                      <option value="">Default (High)</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button type="submit" disabled={submitting} className="bg-[#004B8D] hover:bg-[#003a6d] h-10 text-sm" data-testid="submit-ticket-btn">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    Submit Ticket
                  </Button>
                  <Button type="button" variant="outline" className="h-10 text-sm" onClick={() => setShowForm(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2" data-testid="support-filters">
          {[
            { id: 'all', label: 'All' },
            { id: 'open', label: 'Open', count: counts.open },
            { id: 'in_progress', label: 'In Progress', count: counts.in_progress },
            { id: 'resolved', label: 'Resolved', count: counts.resolved },
          ].map(f => (
            <Button
              key={f.id}
              variant={filter === f.id ? 'default' : 'outline'}
              size="sm"
              className={`h-8 text-xs ${filter === f.id ? 'bg-[#004B8D] hover:bg-[#003a6d]' : ''}`}
              onClick={() => setFilter(f.id)}
              data-testid={`filter-${f.id}`}
            >
              {f.label}
              {f.count > 0 && <span className="ml-1.5 opacity-70">({f.count})</span>}
            </Button>
          ))}
        </div>

        {/* Ticket List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#004B8D]" />
          </div>
        ) : tickets.length === 0 ? (
          <Card className="border-dashed border-slate-300" data-testid="no-tickets">
            <CardContent className="p-8 text-center">
              <MessageSquare className="w-8 h-8 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No support tickets</p>
              <p className="text-xs text-slate-400 mt-1">Submit a ticket to get help from our team.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="tickets-list">
            {tickets.map(ticket => {
              const status = STATUS_STYLE[ticket.status] || STATUS_STYLE.open;
              const prio = PRIORITY_STYLE[ticket.priority] || PRIORITY_STYLE.normal;
              return (
                <Card key={ticket.ticket_id} className="border-slate-200 hover:shadow-sm transition-shadow" data-testid={`ticket-${ticket.ticket_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="text-sm font-semibold text-slate-900 truncate">{ticket.subject}</span>
                          <Badge className={`text-[10px] px-1.5 py-0 h-4 border ${status.color}`} data-testid={`ticket-status-${ticket.ticket_id}`}>
                            {status.label}
                          </Badge>
                          <Badge className={`text-[10px] px-1.5 py-0 h-4 border ${prio.color}`} data-testid={`ticket-priority-${ticket.ticket_id}`}>
                            {prio.label}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-500 line-clamp-2">{ticket.message}</p>
                        <p className="text-[11px] text-slate-400 mt-1.5">
                          {new Date(ticket.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </HostAppShell>
  );
};

export default HostSupportCenterPage;
